"use client";

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import FacilityRequestModal from '@/components/common/FacilityRequestModal';
import ClinicianApplicationModal from '@/components/common/ClinicianApplicationModal';

export default function HeroSection() {
  const [headlineIndex, setHeadlineIndex] = useState(0);
  const [isHydrated, setIsHydrated] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [facilityModalOpen, setFacilityModalOpen] = useState(false);
  const [clinicianModalOpen, setClinicianModalOpen] = useState(false);

  const headlines = [
    "Permanent healthcare hiring, simplified",
    "Hire full-time clinicians faster",
    "Permanent placements for nurses, allied, and providers"
  ];

  useEffect(() => {
    setIsHydrated(true);
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setHeadlineIndex((prev) => (prev + 1) % headlines?.length);
        setIsTransitioning(false);
      }, 300);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  if (!isHydrated) {
    return (
      <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0a0f0d] via-[#1a2420] to-[#26342f] pt-32 pb-20 overflow-hidden">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h1 className="text-6xl md:text-7xl font-bold text-white mb-8 leading-tight">
            {headlines?.[0]}
          </h1>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-white via-[#f8faf9] to-[#f0f4f2] pt-32 pb-24 overflow-hidden">
        {/* Enhanced Grid Overlay for Hero Section */}
        <div 
          className="absolute inset-0 pointer-events-none z-0 backdrop-blur-[5px]"
          style={{
            backgroundImage: 'linear-gradient(#26342f 1.5px, transparent 1.5px), linear-gradient(90deg, #26342f 1.5px, transparent 1.5px)',
            backgroundSize: '60px 60px',
            opacity: 0.03
          }}
        />
        {/* Enhanced Background Elements */}
        <div className="absolute inset-0 opacity-[0.07]">
          <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-[#3a4d46] rounded-full blur-[120px] animate-pulse-glow"></div>
          <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-[#4d6359] rounded-full blur-[100px] animate-pulse-glow" style={{ animationDelay: '2s' }}></div>
        </div>
        
        {/* Subtle Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.015]" style={{
          backgroundImage: 'linear-gradient(#3a4d46 1px, transparent 1px), linear-gradient(90deg, #3a4d46 1px, transparent 1px)',
          backgroundSize: '80px 80px'
        }}></div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 w-full">
          <div className="text-center mb-20">
            {/* Eyebrow Text */}
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-8 rounded-full bg-white/80 backdrop-blur-sm border border-[#3a4d46]/20 shadow-sm animate-fade-in-up">
              <div className="w-2 h-2 bg-[#3a4d46] rounded-full animate-pulse"></div>
              <span className="text-sm font-semibold text-[#3a4d46] tracking-wide">PERMANENT PLACEMENTS ONLY</span>
            </div>

            {/* Rotating Headlines with Better Typography */}
            <h1 className={`text-5xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-[#26342f] mb-6 leading-[1.1] tracking-tight h-[11rem] sm:h-[11rem] md:h-[13.2rem] lg:h-[15.4rem] xl:h-[17.6rem] flex items-center justify-center transition-all duration-300 ease-in-out ${
              isTransitioning ? 'opacity-0 transform translate-y-2' : 'opacity-100 transform translate-y-0'
            }`}>
              {headlines?.[headlineIndex]}
            </h1>

            {/* Subheadline with Better Spacing */}
            <p className="text-lg sm:text-xl md:text-2xl text-[#3a4d46] max-w-3xl mx-auto mb-12 leading-relaxed font-normal">
              Vetted candidates. Clear process. Support through start date.
            </p>

            {/* CTA Buttons with Enhanced Hierarchy */}
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <button
                onClick={() => setFacilityModalOpen(true)}
                className="group relative px-10 py-5 text-lg font-semibold text-white bg-gradient-to-r from-[#26342f] to-[#3a4d46] rounded-2xl transition-all duration-300 hover:shadow-[0_0_40px_rgba(93,211,158,0.4)] hover:-translate-y-1 w-full sm:w-auto overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-[#3a4d46] to-[#4d6359] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <span className="relative flex items-center gap-2">
                  Request candidates
                  <Icon name="ArrowRightIcon" size={20} className="group-hover:translate-x-1 transition-transform duration-300" />
                </span>
              </button>
              <button
                onClick={() => setClinicianModalOpen(true)}
                className="px-10 py-5 text-lg font-semibold text-[#26342f] bg-white border-2 border-[#3a4d46]/30 rounded-2xl hover:bg-[#f8faf9] hover:border-[#3a4d46] transition-all duration-300 hover:shadow-lg hover:-translate-y-1 w-full sm:w-auto"
              >
                Apply for jobs
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Modals */}
      <FacilityRequestModal isOpen={facilityModalOpen} onClose={() => setFacilityModalOpen(false)} />
      <ClinicianApplicationModal isOpen={clinicianModalOpen} onClose={() => setClinicianModalOpen(false)} />
    </>
  );
}